import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { useRequestCompileContext } from 'client/components/schema/requests/compile-context/RequestCompileContext';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { createDraftRequestValidator } from 'common/dto/DraftRequestDto';
import { ICreateRequestDto } from 'common/dto/RequestDto';
import { IProcedure } from 'common/schema/Procedure';
import { IRequest } from 'common/schema/request/Request';
import { FormikProps, useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

interface IDraftSaveOptions {
  /**
   * Dati della domanda (al momento non utilizzati)
   */
  procedure: IProcedure | undefined;
  /**
   * Comportamento desiderato al "Salva". Può essere utilizzato per chiamare
   * differente API (create/update) oppure per non avere side-effect in caso
   * di pagina di anteprima.
   */
  run: (values: ICreateRequestDto) => Promise<AxiosResponse<IRequest>>;
}

export type RunDraftSave = (formik: FormikProps<any>) => Promise<void>;

/**
 * È statico e non dipende dalla procedura.
 */
const draftValidator = createDraftRequestValidator();

/**
 * Gestisce il comportamento di salvataggio in bozza a prescindere
 * dalla pagina in cui viene utilizzata.
 *
 * In caso di nuova istanza, effettua anche il redirect alla pagina dedicata.
 */
export function useDraftSave({ procedure, run }: IDraftSaveOptions) {
  const compileContext = useRequestCompileContext();
  const history = useHistory();

  const draftSave: RunDraftSave = async (formik: FormikProps<any>) => {
    // Capiamo se l'istanza è nuova in base all'ID. Questo viene settato negli
    // `initialValues` di formik in caso sia presente
    const isNew = formik.initialValues.id == null;

    const rawValues = formik.values;
    rawValues.sendRightNow = false;
    try {
      const values = await draftValidator.validate(rawValues, {
        abortEarly: false,
        context: {
          request: compileContext.requestPreview
        }
      });

      const result = await run({ ...values });

      if (isNew) {
        // Creazione di una nuova istanza e salvataggio in bozza della stessa,
        // con redirect alla pagina dedicata.
        history.push(`/richieste/${result.data!.id}/modulo`);
        message.success('Istanza salvata in bozza.');
      } else {
        // Aggiornamento
        message.success('Istanza salvata in bozza.');
      }
    } catch (e) {
      // TODO Gestire. Caso di errore di validazione OPPURE errore nell'invio
      console.error('Errore di validazione non gestito', e);
    }
  };

  return [draftSave] as const;
}
